import { useEffect, useState } from "react";
import axios from "axios";
import { Quote, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialsSection({ variant = "light" }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([
          axios.get(`${API}/testimonials/public`),
          axios.get(`${API}/stats/public`),
        ]);
        setItems(t.data || []);
        setStats(s.data || null);
      } catch {
        // silent: solo es prueba social
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded) return null;
  if (items.length === 0 && (!stats || stats.empresas_escaneadas < 10)) return null;

  const isDark = variant === "dark";
  return (
    <section
      data-testid="testimonials-section"
      className={`px-6 py-20 sm:py-24 ${isDark ? "bg-slate-900 text-white" : "bg-white border-y border-slate-200"}`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-12">
          <p className={`text-xs font-semibold tracking-widest uppercase ${isDark ? "text-blue-300" : "text-[#1E3A8A]"}`}>
            Prueba social
          </p>
          <h2 className={`mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
            PYMEs chilenas que ya se están adecuando a la Ley 21.719.
          </h2>
        </div>

        {stats && (
          <div data-testid="live-stats" className="grid grid-cols-3 gap-4 sm:gap-8 mb-12 max-w-3xl">
            <Stat
              isDark={isDark}
              value={stats.empresas_escaneadas}
              label="Empresas escaneadas"
            />
            <Stat
              isDark={isDark}
              value={stats.documentos_generados}
              label="Documentos generados"
            />
            <Stat
              isDark={isDark}
              value={stats.pro_activos}
              label="Suscriptores Pro activos"
            />
          </div>
        )}

        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {items.slice(0, 3).map((t) => (
              <article
                key={t.id}
                data-testid={`testimonial-${t.id}`}
                className={`rounded-2xl p-6 ${isDark ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-200"}`}
              >
                <Quote className={`w-5 h-5 mb-3 ${isDark ? "text-blue-300" : "text-[#1E3A8A]"}`} />
                <p className={`text-sm leading-relaxed ${isDark ? "text-white/90" : "text-slate-700"}`}>
                  &ldquo;{t.texto}&rdquo;
                </p>
                <footer className={`mt-5 pt-4 border-t ${isDark ? "border-white/10" : "border-slate-200"}`}>
                  <p className={`font-semibold text-sm ${isDark ? "text-white" : "text-slate-900"}`}>
                    {t.nombre}
                  </p>
                  <p className={`text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
                    {[t.cargo, t.empresa].filter(Boolean).join(" · ")}
                    {t.rubro && <span className="ml-1 opacity-80">· {t.rubro}</span>}
                  </p>
                </footer>
              </article>
            ))}
          </div>
        )}

        <div className={`mt-10 flex items-center gap-2 text-xs ${isDark ? "text-white/60" : "text-slate-500"}`}>
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Cifras en tiempo real de nuestra base de datos.</span>
        </div>
      </div>
    </section>
  );
}

function Stat({ isDark, value, label }) {
  return (
    <div>
      <div
        data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
        className={`font-display text-3xl sm:text-4xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}
      >
        {Number(value || 0).toLocaleString("es-CL")}
      </div>
      <div className={`mt-1 text-xs sm:text-sm ${isDark ? "text-white/60" : "text-slate-500"}`}>
        {label}
      </div>
    </div>
  );
}
